import { Tooltip as MantineTooltip, createStyles } from '@mantine/core';
import type { ReactNode } from 'react';

import { textStyles } from '~/domains/design-system/Text';

type TooltipVariant = 'default' | 'compact';

type TooltipProps = {
  isRendered?: boolean;
  labels?: {
    label: string | ReactNode;
    condition?: boolean;
  }[];
  fontSize?: number;
  variant?: TooltipVariant;
} & Partial<React.ComponentProps<typeof MantineTooltip>>;

const useStyles = createStyles(
  (theme, { fontSize, variant }: { fontSize: number; variant: TooltipVariant }) => ({
    tooltip: {
      ...(variant === 'default' && {
        border: `1px solid ${theme.colors.gray2[1]}`,
        color: theme.black,
        backgroundColor: theme.white,
        fontSize: fontSize || 'inherit',
      }),
      ...(variant === 'compact' && {
        backgroundColor: theme.colors.gray2[8],
        color: theme.white,
        border: 'none',
        fontSize: theme.fontSizes.xs,
      }),
    },
    arrow: {
      ...(variant === 'default' && {
        border: `1px solid ${theme.colors.gray2[0]}`,
      }),
      ...(variant === 'compact' && {
        display: 'none',
      }),
    },
  })
);

export function Tooltip({
  isRendered = true,
  labels,
  fontSize,
  variant = 'default',
  events,
  ...rest
}: TooltipProps) {
  const { children } = rest;
  const { classes } = useStyles({ fontSize, variant });

  if (!isRendered) return <div className={rest.className}>{children}</div>;

  const pickLabel = labels?.find(({ condition, label }) => {
    if (typeof condition === 'undefined') return label;
    return condition;
  })?.label;

  const defaultEvents = {
    hover: true,
    focus: true,
    touch: true,
  };

  return (
    <MantineTooltip
      events={events || defaultEvents}
      withinPortal={rest.withinPortal ?? true}
      zIndex={rest.zIndex ?? 9999}
      label={pickLabel}
      classNames={classes}
      {...rest}
    >
      {children}
    </MantineTooltip>
  );
}
